import { useEffect, useState } from "react";
import { TrendingDown, ShieldCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { EfficiencySparkline } from "@/components/EfficiencySparkline";

/**
 * Trust Centre hero — the gross / net pair.
 *
 *  - **Drift £ caught**  (gross) — budget-at-risk surfaced by Drift Watches.
 *  - **£ saved by remediation** (net) — exposure closed out via completed plans.
 *
 * Both come from `/api/public/trust-savings/headline` in one round-trip.
 * Each card hides itself when its £ figure is 0 so the page never makes
 * a misleading "£0 caught/saved" claim.
 */
const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

export function TrustSavingsHero() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/public/trust-savings/headline")
      .then(({ data: d }) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        // Soft-fail — never break a marketing page on a backend hiccup.
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <section className="mx-auto max-w-5xl px-6 pt-8">
        <Loader2 className="animate-spin text-ek-purple/60" size={18} />
      </section>
    );
  }
  if (!data) return null;

  const drift = data.drift || {};
  const remediation = data.remediation || {};
  const showDrift = (drift.total_gbp_caught || 0) > 0;
  const showRemediation = (remediation.total_gbp_saved || 0) > 0;
  if (!showDrift && !showRemediation) return null;

  return (
    <section
      className="mx-auto mt-6 max-w-5xl px-6"
      data-testid="trust-savings-hero"
    >
      <div className="grid gap-3 md:grid-cols-2">
        {showDrift && (
          <Card
            kind="gross"
            icon={<TrendingDown size={22} />}
            label="Drift caught"
            amount={drift.total_gbp_caught}
            events={drift.events_count}
            anchor={drift.anchor_at}
            footnote="Gross programme budget-at-risk surfaced for action."
            testid="trust-drift-card"
            amountTestid="trust-drift-savings-amount"
            unmapped={drift.unmapped_events}
          />
        )}
        {showRemediation && (
          <Card
            kind="net"
            icon={<ShieldCheck size={22} />}
            label="Saved by remediation"
            amount={remediation.total_gbp_saved}
            events={remediation.events_count}
            anchor={remediation.anchor_at}
            footnote="Net £ exposure closed out via completed, owner-approved plans."
            testid="trust-remediation-card"
            amountTestid="trust-remediation-savings-amount"
            unmapped={remediation.unmapped_events}
          />
        )}
      </div>
      {showDrift && showRemediation && (
        <EfficiencyBar
          gross={drift.total_gbp_caught}
          net={remediation.total_gbp_saved}
        />
      )}
      {showDrift && showRemediation && (
        <p
          className="mt-3 text-center text-[11px] italic text-ek-ink/50"
          data-testid="trust-savings-narrative"
        >
          Gross exposure surfaced · net exposure closed · both anonymised
          platform-wide.
        </p>
      )}
    </section>
  );
}

/**
 * Remediation efficiency = £ saved / £ caught × 100, capped at 100%.
 *
 * Why cap at 100%: a customer can legitimately remediate more than was
 * caught in a given window (some closures land for catches recorded
 * before the anchor was reset). A figure > 100% would confuse readers
 * more than it informs them.
 *
 * Why a percentage and not a ratio: CFOs and procurement teams read
 * percentages instantly. A ratio (£1 saved per £1 caught) reads as
 * arithmetic. We're optimising for a single demo-friendly KPI.
 */
function EfficiencyBar({ gross, net }) {
  const raw = gross > 0 ? (net / gross) * 100 : 0;
  const pct = Math.max(0, Math.min(100, raw));
  // Three bands chosen to align with the green/amber/red colour story used
  // elsewhere in the app (PIS, RRQS).
  const band =
    pct >= 75 ? "green" : pct >= 50 ? "amber" : "red";
  const colour =
    band === "green"
      ? "bg-emerald-500"
      : band === "amber"
        ? "bg-amber-400"
        : "bg-rose-400";
  const label =
    band === "green"
      ? "Strong"
      : band === "amber"
        ? "Healthy"
        : "Catching faster than closing";

  return (
    <div
      className="mt-3 rounded-2xl border border-slate-200 bg-white px-5 py-4"
      data-testid="trust-efficiency-bar"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-ek-ink/65">
            Remediation efficiency
          </p>
          <span
            className={`rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold ${
              band === "green"
                ? "bg-emerald-100 text-emerald-700"
                : band === "amber"
                  ? "bg-amber-100 text-amber-700"
                  : "bg-rose-100 text-rose-700"
            }`}
          >
            {label}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <EfficiencySparkline days={30} />
          <p
            className="font-display text-2xl font-semibold text-ek-ink"
            data-testid="trust-efficiency-pct"
          >
            {pct.toFixed(0)}%
          </p>
        </div>
      </div>
      <div
        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Remediation efficiency"
      >
        <div
          className={`h-full ${colour} transition-[width] duration-700`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-2 text-[10.5px] italic leading-relaxed text-ek-ink/55">
        Net £ closed ÷ gross £ surfaced. Capped at 100% — a window where the
        closure rate matches or exceeds the catch rate reads as &ldquo;fully
        keeping up&rdquo;.
      </p>
    </div>
  );
}

// Back-compat alias — the old name still works in any older imports.
export { TrustSavingsHero as DriftSavingsHero };

function Card({
  kind,
  icon,
  label,
  amount,
  events,
  anchor,
  footnote,
  testid,
  amountTestid,
  unmapped,
}) {
  const accent = kind === "net"
    ? { ring: "border-emerald-200", bg: "from-emerald-50 to-transparent", chip: "text-emerald-700 bg-emerald-100/70", iconBg: "bg-emerald-100 text-emerald-700" }
    : { ring: "border-ek-purple/20", bg: "from-ek-purple/5 to-transparent", chip: "text-ek-purple bg-ek-purple/10", iconBg: "bg-ek-purple/10 text-ek-purple" };
  return (
    <div
      className={`flex flex-col gap-3 rounded-2xl border bg-gradient-to-br px-6 py-5 ${accent.ring} ${accent.bg}`}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${accent.iconBg}`}>
          {icon}
        </span>
        <div className="min-w-0">
          <p className={`text-[10px] font-bold uppercase tracking-wider ${accent.chip} rounded-full px-2 py-0.5 inline-block`}>
            {label}
          </p>
          <p
            className="mt-1 font-display text-3xl font-semibold text-ek-ink sm:text-[2rem]"
            data-testid={amountTestid}
          >
            {GBP.format(amount)}
          </p>
          <p className="mt-1 text-[11.5px] leading-relaxed text-ek-ink/65">
            since {formatDate(anchor)} ·{" "}
            <span className="text-ek-ink/45">
              {events} event{events === 1 ? "" : "s"}
              {unmapped > 0 && ` · ${unmapped} non-GBP excluded`}
            </span>
          </p>
        </div>
      </div>
      <p className="text-[10.5px] italic leading-relaxed text-ek-ink/55">
        {footnote}
      </p>
    </div>
  );
}
